import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import type { BrandKit } from '@/types/brand-kit';
import { logger } from '@/utils/logger';

interface BrandKitContextValue {
  brandKit: BrandKit | null;
  isLoading: boolean;
  refreshBrandKit: () => Promise<void>;
  loadBrandKitIfNeeded: () => Promise<void>;
}

const BrandKitContext = createContext<BrandKitContextValue | null>(null);

export const BrandKitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [brandKit, setBrandKit] = useState<BrandKit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Only load brand kit when explicitly requested
  const loadBrandKitIfNeeded = useCallback(async () => {
    if (!user || hasLoaded || isLoading) return;

    setIsLoading(true);
    try {
      // F-082 (UAT 2026-06-13): lazy-import so the heavyweight image-compression
      // dependency chain (browser-image-compression ~280KB, pulled in transitively
      // by BrandKitService) stays OUT of the root critical path. This context is
      // mounted app-wide but the kit only loads on demand — eager-importing the
      // service forced ~280KB into the entry graph, worsening the cold-boot white
      // screen (F-082). Imported here, inside the on-demand callback, instead.
      const { brandKitService } = await import('@/services/data/BrandKitService');
      const kit = await brandKitService.getBrandKit();
      setBrandKit(kit);
      setHasLoaded(true);
    } catch (err) {
      // Silently fail - brand kit is optional functionality
      logger.debug('Brand kit not available:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, hasLoaded, isLoading]);

  const refreshBrandKit = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // F-082: lazy-import (see loadBrandKitIfNeeded) — keep BrandKitService's
      // image-compression dep out of the root entry graph.
      const { brandKitService } = await import('@/services/data/BrandKitService');
      const kit = await brandKitService.getBrandKit();
      setBrandKit(kit);
      setHasLoaded(true);
    } catch (err) {
      logger.debug('Brand kit refresh failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Clear data when user logs out
  useEffect(() => {
    if (!user) {
      setBrandKit(null);
      setHasLoaded(false);
    }
  }, [user]);

  return (
    <BrandKitContext.Provider
      value={{ brandKit, isLoading, refreshBrandKit, loadBrandKitIfNeeded }}
    >
      {children}
    </BrandKitContext.Provider>
  );
};

export const useBrandKitContext = (): BrandKitContextValue => {
  const context = useContext(BrandKitContext);
  if (!context) {
    throw new Error('useBrandKitContext must be used within a BrandKitProvider');
  }
  return context;
};
